import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select } from '../ui/newSelect';
import { Alert, AlertDescription } from '../ui/alert';
import { Checkbox } from '../ui/checkbox';
import { Actions as CONSULTANT_ACTIONS } from '../../store/actions/consultantClientsActions';
import { Actions as AGREEMENTS } from '../../store/actions/agreementsActions';
import { toggleAlertMessage } from '../../store/action';
import { BILLING_CURRENCY_OPTIONS } from '../utils/agreementLineItemHelpers';
import { buildAgreementCountryPayload } from '../Modals/agreementsUtils';
import { friendlyConsultantClientError } from '../utils/consultantClientUtils';

const PLAN_OPTIONS = ['discover', 'measure', 'execute', 'coordinate', 'govern'].map((value) => ({
  value,
  label: value.replace(/\b\w/g, (letter) => letter.toUpperCase()),
}));

const FTE_OPTIONS = ['1-10', '11-50', '51-100', '101-200', '201-250', '250-plus'].map((value) => ({
  value,
  label: `${value} FTE`,
}));

const INITIAL_FORM = {
  billingRelationship: 'consultant_billed',
  customerOrganisationName: '',
  customerEmail: '',
  customerFirstName: '',
  customerLastName: '',
  customerRegisteredCountry: '',
  billingCurrency: '£',
  planId: 'measure',
  fteBand: '1-10',
  addOnIds: [],
  payMode: 'monthly_cycle',
  termMonths: 12,
  autoRenew: true,
  directPayMode: 'invoice',
};

const AddConsultantClientWizard = ({
  open,
  onClose,
  onSuccess,
  countries,
  consultantParentId,
  createConsultantClient,
  getMyAgreements,
  getConsultantPricingSchedule,
  showAlert,
}) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [pricingSchedule, setPricingSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const isDirect = form.billingRelationship === 'direct_billed';

  useEffect(() => {
    if (!open) {
      setForm(INITIAL_FORM);
      setStep(1);
      setPricingSchedule(null);
      return;
    }
    setScheduleLoading(true);
    const load = async () => {
      let schedule = null;
      if (consultantParentId) {
        const data = await getMyAgreements(consultantParentId);
        const agreements = data?.agreements || [];
        const master = agreements.find(
          (a) => a.type === 'Consultant' && (a.status === 'complete' || a.active === true),
        );
        schedule = master?.consultantPricingSchedule || null;
      }
      if (!schedule) {
        schedule = await getConsultantPricingSchedule(form.billingCurrency);
      }
      setPricingSchedule(schedule || null);
      setScheduleLoading(false);
    };
    load();
  }, [open, consultantParentId, form.billingCurrency, getMyAgreements, getConsultantPricingSchedule]);

  const addOnOptions = useMemo(() => pricingSchedule?.addOns || [], [pricingSchedule]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleAddOn = (addOnId) => {
    setForm((f) => {
      const set = new Set(f.addOnIds || []);
      if (set.has(addOnId)) set.delete(addOnId);
      else set.add(addOnId);
      return { ...f, addOnIds: [...set] };
    });
  };

  const validate = () => {
    if (!form.customerOrganisationName.trim()) return 'Enter the client organisation name.';
    if (!form.customerEmail.trim()) return 'Enter the client contact email.';
    if (!form.customerFirstName.trim() || !form.customerLastName.trim()) return 'Enter the client contact name.';
    if (!form.planId || !form.fteBand) return 'Select a Plan and FTE band.';
    if (!pricingSchedule) return 'Your Consultant Pricing Schedule is not available yet.';
    return null;
  };

  const onSubmit = async () => {
    const err = validate();
    if (err) {
      showAlert(true, err, 'error', 5000);
      return;
    }

    setSaving(true);
    const body = {
      billingRelationship: form.billingRelationship,
      customerOrganisationName: form.customerOrganisationName.trim(),
      customerEmail: form.customerEmail.trim(),
      customerFirstName: form.customerFirstName.trim(),
      customerLastName: form.customerLastName.trim(),
      billingCurrency: form.billingCurrency,
      planId: form.planId,
      fteBand: form.fteBand,
      addOnIds: form.addOnIds || [],
      planType: 'catalog_plan',
      termMonths: Number(form.termMonths) || 12,
      autoRenew: !!form.autoRenew,
      ...buildAgreementCountryPayload({
        incorporationCountryId: form.customerRegisteredCountry,
        billingCountryId: form.customerRegisteredCountry,
        countries,
      }),
    };

    if (isDirect) {
      body.payMode = form.directPayMode;
    } else {
      body.payMode = form.payMode;
    }

    const result = await createConsultantClient(body);
    setSaving(false);

    if (result.ok) {
      const msg = isDirect
        ? `Invitation sent to ${form.customerEmail}. The client must accept the order form and complete payment before anyone can add facilities or usage. Resend from Client Portfolio if needed.`
        : 'Client created. Activate it from Client Portfolio when you are ready — facilities and usage stay locked until then.';
      showAlert(true, msg, 'success', null);
      if (result.data?.agreementUrl) window.open(result.data.agreementUrl, '_blank', 'noopener');
      onSuccess?.(result.data);
    } else {
      showAlert(true, friendlyConsultantClientError(result.error || result.message), 'error', 6000);
    }
  };

  const countryOptions = (countries || []).map((c) => ({ value: c.id, label: c.name }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto bg-card text-card-foreground'>
        <DialogHeader>
          <DialogTitle>Add client</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className='space-y-4 p-4'>
            <div>
              <Label>Who pays?</Label>
              <Select
                value={form.billingRelationship}
                onValueChange={(v) => update('billingRelationship', v)}
                options={[
                  { value: 'consultant_billed', label: 'Billed to you (portfolio points)' },
                  { value: 'direct_billed', label: 'Client pays CYF directly (standard catalogue price)' },
                ]}
              />
            </div>
            {form.billingRelationship === 'consultant_billed' && (
              <Alert variant='info'>
                <AlertDescription className='text-xs text-foreground'>
                  Choose one Plan and FTE band from your accepted Consultant Pricing Schedule. Optional add-ons are
                  snapshotted at your current programme level’s net price.
                </AlertDescription>
              </Alert>
            )}
            {form.billingRelationship === 'direct_billed' && (
              <Alert variant='info'>
                <AlertDescription className='text-xs text-foreground'>
                  The client’s order form shows the standard Plan × FTE catalogue price (not your consultant discount),
                  a 12- or 36-month term, and 180 days’ notice. The client pays CYF and earns no portfolio points.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {step === 2 && (
          <div className='space-y-4 p-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              <div className='md:col-span-2'>
                <Label>Organisation name *</Label>
                <Input
                  value={form.customerOrganisationName}
                  onChange={(e) => update('customerOrganisationName', e.target.value)}
                />
              </div>
              <div>
                <Label>First name *</Label>
                <Input value={form.customerFirstName} onChange={(e) => update('customerFirstName', e.target.value)} />
              </div>
              <div>
                <Label>Last name *</Label>
                <Input value={form.customerLastName} onChange={(e) => update('customerLastName', e.target.value)} />
              </div>
              <div className='md:col-span-2'>
                <Label>Email *</Label>
                <Input type='email' value={form.customerEmail} onChange={(e) => update('customerEmail', e.target.value)} />
              </div>
              <div>
                <Label>Country</Label>
                <Select
                  value={form.customerRegisteredCountry}
                  onValueChange={(v) => update('customerRegisteredCountry', v)}
                  placeholder='Select country'
                  options={countryOptions}
                  searchable
                />
              </div>
              <div>
                <Label>Billing currency</Label>
                <Select
                  value={form.billingCurrency}
                  onValueChange={(v) => update('billingCurrency', v)}
                  options={BILLING_CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }))}
                />
              </div>
            </div>

            <div className='space-y-3 border-t border-border pt-4'>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label>Plan *</Label>
                  <Select value={form.planId} onValueChange={(v) => update('planId', v)} options={PLAN_OPTIONS} />
                </div>
                <div>
                  <Label>Client FTE band *</Label>
                  <Select value={form.fteBand} onValueChange={(v) => update('fteBand', v)} options={FTE_OPTIONS} />
                </div>
              </div>

              <div>
                <Label>Add-ons (optional)</Label>
                {scheduleLoading ? (
                  <p className='text-xs text-muted-foreground mt-1'>Loading catalogue add-ons…</p>
                ) : addOnOptions.length === 0 ? (
                  <p className='text-xs text-muted-foreground mt-1'>No add-ons are offered in this currency.</p>
                ) : (
                  <div className='mt-2 space-y-2'>
                    {addOnOptions.map((addOn) => {
                      const priceLabel =
                        addOn.standardAnnualPrice != null
                          ? `${form.billingCurrency}${Number(addOn.standardAnnualPrice).toFixed(2)} / year`
                          : `${form.billingCurrency}${Number(addOn.standardMonthlyPrice || 0).toFixed(2)} / month`;
                      return (
                        <label key={addOn.id} className='flex items-start gap-2 text-sm cursor-pointer'>
                          <Checkbox
                            checked={(form.addOnIds || []).includes(addOn.id)}
                            onCheckedChange={() => toggleAddOn(addOn.id)}
                          />
                          <span>
                            <span className='font-medium'>{addOn.name}</span>
                            <span className='text-muted-foreground'> — {priceLabel}</span>
                            {isDirect ? null : (
                              <span className='block text-xs text-muted-foreground'>
                                Consultant net price uses your programme level when the client is billed to you.
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className='grid grid-cols-2 gap-3'>
                {!isDirect && (
                  <div>
                    <Label>Pay mode</Label>
                    <Select
                      value={form.payMode}
                      onValueChange={(v) => update('payMode', v)}
                      options={[
                        { value: 'monthly_cycle', label: 'Monthly cycle' },
                        { value: 'pay_in_full', label: 'Pay in full' },
                      ]}
                    />
                  </div>
                )}
                {isDirect && (
                  <div>
                    <Label>Client pay mode</Label>
                    <Select
                      value={form.directPayMode}
                      onValueChange={(v) => update('directPayMode', v)}
                      options={[
                        { value: 'invoice', label: 'Invoice' },
                        { value: 'checkout', label: 'Card checkout' },
                        { value: 'both', label: 'Invoice or checkout' },
                      ]}
                    />
                  </div>
                )}
                <div>
                  <Label>Term (months)</Label>
                  <Select
                    value={form.termMonths}
                    onValueChange={(value) => update('termMonths', Number(value))}
                    options={[
                      { value: 12, label: '12 months' },
                      { value: 36, label: '36 months (extra 5% discount)' },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {step > 1 && (
            <Button variant='outline' onClick={() => setStep(1)} disabled={saving}>
              Back
            </Button>
          )}
          {step === 1 ? (
            <Button onClick={() => setStep(2)}>Continue</Button>
          ) : (
            <Button loading={saving} onClick={onSubmit} disabled={scheduleLoading || !pricingSchedule}>
              Create client
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  countries: state.organisation?.countries,
  consultantParentId: state.profile?.details?.consultantAdmin,
});

const mapDispatchToProps = (dispatch) => ({
  createConsultantClient: (body) => dispatch(CONSULTANT_ACTIONS.createConsultantClient(body)),
  getMyAgreements: (organisationId) => dispatch(AGREEMENTS.getMyAgreements(organisationId)),
  getConsultantPricingSchedule: (currency) => dispatch(AGREEMENTS.getConsultantPricingSchedule(currency)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddConsultantClientWizard);
